/**
 * Labs Core Framework API
 *
 * Handles lab experiment enrollment, eligibility, and approvals.
 * These are framework-level APIs used across all lab experiments.
 *
 * Also loads and includes all lab experiment-specific APIs.
 */
export const LabsCore = (flujax) => ({
  // Small utility for registering new endpoints to $res.labs[key] so we can
  // provide the reference to flujax. This is to get around _always_ including
  // labs endpoints even if a user is not enrolled.
  registerLabEndpoints: (key, routesCallback) => {
    const { $res } = useNuxtApp()

    if (Object.hasOwn($res.labs, key)) {
      return
    }

    $res.labs[key] = routesCallback(flujax)
  },

  // Core framework APIs below
  /**
   * Get all available lab experiments
   * @returns {Promise<Array>} List of all labs
   */
  async availableLabs () {
    return flujax.get('/labs')
  },

  /**
   * Get lab experiments the current user is eligible for (enrolled in)
   * @returns {Promise<Array>} List of eligible labs with enrollment data
   */
  async eligibleLabs () {
    return flujax.get('/labs/eligible')
  },

  /**
   * Get customer-level lab approvals
   * @returns {Promise<Array>} Customer lab approval records
   */
  async customerLabApprovals () {
    return flujax.get('/labs/customer')
  },

  /**
   * Get user-level lab approvals
   * @returns {Promise<Array>} User lab approval records
   */
  async userLabApprovals () {
    return flujax.get('/labs/user')
  },

  /**
   * Approve a lab experiment at the customer level
   * @param {number} labId - Lab experiment ID
   * @returns {Promise<Object>} Approval response
   */
  async approveLabForCustomer (labId) {
    return flujax.post(`/labs/${labId}/customer/approve`)
  },

  /**
   * Decline a lab experiment at the customer level
   * @param {number} labId - Lab experiment ID
   * @returns {Promise<Object>} Decline response
   */
  async declineLabForCustomer (labId) {
    return flujax.post(`/labs/${labId}/customer/decline`)
  },

  /**
   * Approve (enroll in) a lab experiment for the current user
   * @param {number} labId - Lab experiment ID
   * @returns {Promise<Object>} Approval response
   */
  async approveLabForUser (labId) {
    return flujax.post(`/labs/${labId}/user/approve`)
  },

  /**
   * Decline (leave) a lab experiment for the current user
   * @param {number} labId - Lab experiment ID
   * @returns {Promise<Object>} Decline response
   */
  async declineLabForUser (labId) {
    return flujax.post(`/labs/${labId}/user/decline`)
  }
})
